import { CortexSetting, LocationVarKey } from "@swivl-mono/types";

import { CortexSettingRow } from "../../Components/CortexSettingRow";
import { TopicCard } from "../../Components/TopicCard";

/**
 * The integration identifiers the location URL service builds links from (ENG-3138).
 *
 * Snowy resolves every customer-facing link from `Cortex*` variables only, so these need an editor
 * of their own — before this page the values existed but nothing could change them, and an operator
 * editing the legacy fields would have drifted away from what the bot actually reads.
 *
 * Swivl-managed, like their legacy counterparts on the org admin subpage: an operator doesn't know
 * their spot slugs or their website provider's client id.
 */
export const TOPIC_VARS: LocationVarKey[] = [
  LocationVarKey.CortexWebsiteProvider,
  LocationVarKey.CortexURLLookupPattern,
  LocationVarKey.CortexStoragelyClientID,
  LocationVarKey.CortexStoragePugSingleLocationBaseURL
];

const PROVIDER: CortexSetting = {
  kind: "select",
  key: "websiteProvider",
  varKey: LocationVarKey.CortexWebsiteProvider,
  label: "Website Provider",
  description: "Who builds this facility's website. Decides which rental-form link the bot sends a customer to.",
  options: [
    { value: "", label: "Not set" },
    { value: "none", label: "None" },
    { value: "golocal", label: "GoLocal" },
    { value: "g5", label: "G5" },
    { value: "storagepug", label: "StoragePug" },
    { value: "storedge", label: "StorEdge" },
    { value: "mariposa", label: "Mariposa" },
    { value: "automatit", label: "Automatit" },
    { value: "storagely", label: "Storagely" }
  ],
  default: ""
};

const LOOKUP_PATTERN: CortexSetting = {
  kind: "text",
  key: "urlLookupPattern",
  varKey: LocationVarKey.CortexURLLookupPattern,
  label: "Website URL Lookup Pattern",
  description: "Path appended to the facility website to reach this location's own page.",
  placeholder: "e.g. /storage-locations/louisiana/addis",
  default: ""
};

const STORAGELY_CLIENT_ID: CortexSetting = {
  kind: "text",
  key: "storagelyClientId",
  varKey: LocationVarKey.CortexStoragelyClientID,
  label: "Storagely Client ID",
  description: "Used in the Storagely rental-form redirect. Without it the bot sends customers to the facility website instead.",
  placeholder: "e.g. 4821",
  default: "",
  showIf: { key: "websiteProvider", equals: "storagely" }
};

const STORAGEPUG_BASE_URL: CortexSetting = {
  kind: "text",
  key: "storagePugBaseUrl",
  varKey: LocationVarKey.CortexStoragePugSingleLocationBaseURL,
  label: "StoragePug Single-Location Base URL",
  description: "For single-location StoragePug sites. Leave blank and the facility website's address is used.",
  placeholder: "e.g. https://storage.example.com",
  default: "",
  showIf: { key: "websiteProvider", equals: "storagepug" }
};

const WEBSITE_SETTINGS: CortexSetting[] = [PROVIDER, LOOKUP_PATTERN, STORAGELY_CLIENT_ID, STORAGEPUG_BASE_URL];

export function WebsiteIntegrationTopic(): React.ReactElement {
  return (
    <>
      <TopicCard title="Website" description="How the bot builds links into this facility's own website.">
        <CortexSettingRow setting={PROVIDER} settings={WEBSITE_SETTINGS} layout="horizontal" />
        <CortexSettingRow setting={LOOKUP_PATTERN} settings={WEBSITE_SETTINGS} layout="horizontal" />
        <CortexSettingRow setting={STORAGELY_CLIENT_ID} settings={WEBSITE_SETTINGS} layout="horizontal" />
        <CortexSettingRow setting={STORAGEPUG_BASE_URL} settings={WEBSITE_SETTINGS} layout="horizontal" />
      </TopicCard>
    </>
  );
}
